/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import {
  enableMicrosoftLogin,
  fidpGitHub,
  fidpGoogle,
  fidpMicrosoft,
} from 'config';
import { SIGNIN } from 'routes';

import GithubLogo from 'assets/images/logo-github.svg';
import GoogleLogo from 'assets/images/google.svg';
import MicrosoftLogo from 'assets/images/microsoft.svg';
import WSO2Logo from 'assets/images/wso2-logo.svg';
import IdentityPlatformLogo from 'assets/images/Identity-platform.svg';
import { AuthSessionVariable, setSessionItem } from 'utils/auth';
import useStyles from './style';

function Login() {
  const classes = useStyles();
  const history = useHistory();

  const handleGithubLogin = () => {
    history.replace(SIGNIN);
    setSessionItem(AuthSessionVariable.FIDP, fidpGitHub);
  };

  const handleGoogleLogin = () => {
    history.replace(SIGNIN);
    setSessionItem(AuthSessionVariable.FIDP, fidpGoogle);
  };

  const handleMicrosoftLogin = () => {
    history.replace(SIGNIN);
    setSessionItem(AuthSessionVariable.FIDP, fidpMicrosoft);
  };

  const gitHubLoginButton = (
    <Button
      id="github-sign-in"
      variant="contained"
      className={clsx(classes.loginBtn, classes.github)}
      onClick={handleGithubLogin}
    >
      <img src={GithubLogo} className={classes.btnIcon} alt="Github Logo" />
      <FormattedMessage
        id="pages.login.button.withGithub"
        defaultMessage="Sign in with GitHub"
      />
    </Button>
  );

  const googleLoginButton = (
    <Button
      id="google-sign-in"
      className={clsx(classes.loginBtn, classes.google)}
      variant="contained"
      onClick={handleGoogleLogin}
    >
      <img src={GoogleLogo} className={classes.btnIcon} alt="Google Logo" />
      <FormattedMessage
        id="pages.login.button.withGoogle"
        defaultMessage="Sign in with Google"
      />
    </Button>
  );

  const microsoftLoginButton = (
    <Button
      id="microsoft-sign-in"
      className={clsx(classes.loginBtn, classes.microsoft)}
      variant="contained"
      onClick={handleMicrosoftLogin}
    >
      <img
        src={MicrosoftLogo}
        className={classes.msftBtnIcon}
        alt="Microsoft Logo"
      />
      <FormattedMessage
        id="pages.login.button.withMicrosoft"
        defaultMessage="Sign in with Microsoft"
      />
    </Button>
  );

  return (
    <>
      <div className={classes.loginBgLogin}>
        <Box display="flex" height={1} flexDirection="column" pt={1}>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            flexGrow={1}
          >
            <Box className={classes.rectangle}>
              <Box>
                <div className={classes.billingPortalLoginLogo}>
                  <img alt="WSO2 logo" src={WSO2Logo} />
                  <Typography style={{ fontSize: 12 }}>
                    <FormattedMessage
                      id="pages.login.application.title"
                      defaultMessage="Cloud Subscription Portal"
                    />
                  </Typography>
                </div>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
              >
                <Box>
                  <Typography className={classes.billingPortalSignInText}>
                    <FormattedMessage
                      id="pages.login.signIn.title"
                      defaultMessage="Sign in to continue"
                    />
                  </Typography>
                  {gitHubLoginButton}
                  {googleLoginButton}
                  {enableMicrosoftLogin && microsoftLoginButton}
                </Box>
              </Box>
              <Box className={classes.moreDetailsContainer}>
                <Typography className={classes.moreDetails}>
                  <FormattedMessage
                    id="pages.login.moreDetails.text"
                    defaultMessage="More details at"
                  />
                </Typography>
                <Typography className={classes.moreDetailsLink}>
                  <a
                    href="https://wso2.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="wso2.com"
                  >
                    <FormattedMessage
                      id="pages.login.moreDetails.linkText"
                      defaultMessage="wso2.com "
                    />
                  </a>
                </Typography>
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              mt={3}
            >
              <Typography>
                <FormattedMessage
                  id="pages.login.idp.message"
                  defaultMessage="Identity Management powered by"
                />
              </Typography>
              <img
                src={IdentityPlatformLogo}
                alt="WSO2 Identity Platform"
                className={classes.identityProviderImage}
              />
            </Box>
          </Box>
        </Box>
      </div>
    </>
  );
}

export default Login;
